import { FaCopy, FaExternalLinkAlt, FaRobot } from 'react-icons/fa';
import { Badge, Button } from '@/components/UI';
import LongTextPreview from './LongTextPreview';
import McpReadinessPanel from './McpReadinessPanel';
import PromptModalButton from './PromptModalButton';
import ScorecardGenerationToolControl from './ScorecardGenerationToolControl';
import SummaryStat from './SummaryStat';
import { featureAuditQueueScopeOptions, toolLaunchUrls } from './constants';
import {
    formatCount,
    formatTimestamp,
    getAuditScopeLabel,
    getScorecardGenerationStatusBadgeVariant,
    getScorecardGenerationToolLabel,
    getWorkbenchPanelReadiness
} from './helpers';
import type {
    AuditMetricScope,
    FeatureAuditArtifact,
    FeatureAuditQueueScope,
    FeatureAuditSessionDetails,
    FeatureAuditSessionList,
    FeatureAuditSessionSummary,
    McpClientReadiness,
    ScorecardGenerationExternalTool
} from './types';

const FeatureAuditQueueScopeControl = ({
    value,
    disabled,
    onChange
}: {
    value: FeatureAuditQueueScope;
    disabled: boolean;
    onChange: (value: FeatureAuditQueueScope) => void;
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs text-zinc-400">Feature Queue</span>
        <div className="flex overflow-hidden rounded-md border border-zinc-700 bg-zinc-950">
            {featureAuditQueueScopeOptions.map((option) => (
                <button
                    aria-pressed={value === option.value}
                    className={`px-3 py-1.5 text-sm transition-colors ${
                        value === option.value ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                    disabled={disabled}
                    key={option.value}
                    title={
                        option.value === 'needs_review'
                            ? 'Queue only features missing or stale external AI curation'
                            : 'Queue every matching diagnostic feature for a full re-audit'
                    }
                    type="button"
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    </div>
);

const FeatureAuditSessionRow = ({
    session,
    selected,
    onSelect
}: {
    session: FeatureAuditSessionSummary;
    selected: boolean;
    onSelect: (sessionId: number) => void;
}) => (
    <button
        className={`flex w-full flex-col gap-1.5 rounded border px-3 py-2 text-left text-sm transition-colors ${
            selected ? 'border-blue-700 bg-blue-950/20' : 'border-zinc-800 bg-zinc-950/40 hover:border-zinc-700'
        }`}
        type="button"
        onClick={() => onSelect(session.id)}
    >
        <div className="flex items-start justify-between gap-2">
            <div
                className="line-clamp-2 min-w-0 font-medium text-zinc-200"
                title={`${session.externalToolLabel ?? getScorecardGenerationToolLabel(session.externalTool)} · ${session.metric ?? 'stock + options'}`}
            >
                {session.externalToolLabel ?? getScorecardGenerationToolLabel(session.externalTool)} · {session.metric ?? 'stock + options'}
            </div>
            <Badge className="shrink-0" variant={getScorecardGenerationStatusBadgeVariant(session.status)}>
                {session.status.replaceAll('_', ' ')}
            </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>#{session.id}</span>
            <span className="ml-auto text-zinc-400">
                {formatCount(session.reviewedItemCount)}/{formatCount(session.totalItemCount)}
            </span>
        </div>
    </button>
);

const FeatureAuditArtifactRow = ({ artifact }: { artifact: FeatureAuditArtifact }) => (
    <div className="border-t border-zinc-800 py-3 first:border-t-0">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <div className="truncate text-sm font-medium text-white" title={artifact.name}>
                {artifact.name}
            </div>
            <Badge variant={artifact.status === 'recommended' ? 'success' : 'secondary'}>{artifact.status}</Badge>
        </div>
        <LongTextPreview text={artifact.summary} title={artifact.name} />
        {artifact.validationSummary && <LongTextPreview text={artifact.validationSummary} title={`${artifact.name} Validation`} />}
    </div>
);

interface FeatureAuditSessionSectionProps {
    auditScope: AuditMetricScope;
    queueScope: FeatureAuditQueueScope;
    tool: ScorecardGenerationExternalTool;
    readiness: McpClientReadiness | undefined;
    sessions: FeatureAuditSessionList | undefined;
    selectedSessionId: number | null;
    details: FeatureAuditSessionDetails | undefined;
    isLoadingSessions: boolean;
    isLoadingDetails: boolean;
    errorMessage: string | undefined;
    actionPending: boolean;
    isStarting: boolean;
    onQueueScopeChange: (value: FeatureAuditQueueScope) => void;
    onToolChange: (value: ScorecardGenerationExternalTool) => void;
    onStart: () => void;
    onOpenExternalTool: () => void;
    onSelectSession: (sessionId: number) => void;
    onCopyPrompt: () => void;
}

const FeatureAuditSessionSection = ({
    auditScope,
    queueScope,
    tool,
    readiness,
    sessions,
    selectedSessionId,
    details,
    isLoadingSessions,
    isLoadingDetails,
    errorMessage,
    actionPending,
    isStarting,
    onQueueScopeChange,
    onToolChange,
    onStart,
    onOpenExternalTool,
    onSelectSession,
    onCopyPrompt
}: FeatureAuditSessionSectionProps) => {
    const activeSession = details?.session;
    const artifacts = details?.artifacts ?? [];
    const panelReadiness = getWorkbenchPanelReadiness(activeSession, details?.connectionReadiness, tool, readiness);

    return (
        <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="font-medium text-white">External AI Feature Audit + Curation</div>
                    <div className="mt-1 text-xs text-zinc-500">
                        {getAuditScopeLabel(auditScope)} for MCP-capable external agents; queues new or stale features by default
                    </div>
                </div>
                <div className="flex flex-col gap-2 lg:items-end">
                    <ScorecardGenerationToolControl disabled={actionPending} value={tool} onChange={onToolChange} />
                    <div className="flex flex-wrap items-center gap-2">
                        <FeatureAuditQueueScopeControl disabled={actionPending} value={queueScope} onChange={onQueueScopeChange} />
                        <Button
                            className="inline-flex items-center"
                            disabled={actionPending}
                            icon={<FaRobot />}
                            isLoading={isStarting}
                            size="sm"
                            variant="primary"
                            onClick={onStart}
                        >
                            Create External Audit Session
                        </Button>
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <McpReadinessPanel readiness={panelReadiness} tool={tool} onOpenExternalTool={onOpenExternalTool} />
            </div>

            {errorMessage && (
                <div className="mb-3 rounded border border-red-900/70 bg-red-950/20 p-3 text-sm text-red-300">{errorMessage}</div>
            )}

            <div className="grid gap-4 lg:grid-cols-[minmax(260px,0.8fr)_minmax(320px,1.2fr)]">
                <div>
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Audit Sessions</div>
                        {isLoadingSessions && <div className="text-xs text-zinc-500">Loading...</div>}
                    </div>
                    <div className="space-y-2">
                        {sessions && sessions.length > 0 ? (
                            sessions.map((session) => (
                                <FeatureAuditSessionRow
                                    key={session.id}
                                    selected={(selectedSessionId ?? sessions[0]?.id) === session.id}
                                    session={session}
                                    onSelect={onSelectSession}
                                />
                            ))
                        ) : (
                            <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3 text-sm text-zinc-500">
                                No external feature-audit sessions yet.
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-3">
                    {isLoadingDetails ? (
                        <div className="text-sm text-zinc-500">Loading session...</div>
                    ) : activeSession ? (
                        <>
                            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                <SummaryStat label="Features" value={activeSession.totalItemCount} />
                                <SummaryStat label="Claimed" value={activeSession.claimedItemCount} />
                                <SummaryStat label="Reviewed" value={activeSession.reviewedItemCount} />
                                <SummaryStat label="Artifacts" value={activeSession.artifactCount} />
                            </div>

                            <div className="rounded border border-zinc-800 bg-zinc-900 p-3 text-xs leading-5 text-zinc-400">
                                <div className="mb-1 flex flex-wrap items-center gap-2">
                                    <span className="font-medium text-zinc-300">AI Workbench</span>
                                    <Badge variant={getScorecardGenerationStatusBadgeVariant(activeSession.status)}>
                                        {activeSession.status.replaceAll('_', ' ')}
                                    </Badge>
                                </div>
                                {activeSession.firstAgentSeenAt ? (
                                    <span>External agent first connected at {formatTimestamp(activeSession.firstAgentSeenAt)}.</span>
                                ) : (
                                    <span>Waiting for an external MCP agent to use the prompt and call Project314 tools.</span>
                                )}
                            </div>

                            <div>
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">External Agent Prompt</div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button icon={<FaCopy />} size="sm" variant="secondary" onClick={onCopyPrompt}>
                                            Copy
                                        </Button>
                                        <PromptModalButton prompt={activeSession.prompt} title="External Feature Audit Prompt" />
                                        {toolLaunchUrls[tool] && (
                                            <Button icon={<FaExternalLinkAlt />} size="sm" variant="secondary" onClick={onOpenExternalTool}>
                                                Open Tool
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Artifacts</div>
                                    <Badge variant="secondary">{artifacts.length}</Badge>
                                </div>
                                {artifacts.length > 0 ? (
                                    <div>
                                        {artifacts.map((artifact) => (
                                            <FeatureAuditArtifactRow artifact={artifact} key={artifact.id} />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3 text-sm text-zinc-500">
                                        No audit artifacts submitted yet.
                                    </div>
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default FeatureAuditSessionSection;
